import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Message,
  MessageForGroup,
  RootEntityType,
  ThreadUser,
  useGetMessageQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  NoMessagesStub,
  useSelectThread,
  useThreads,
  useThreadUser,
  WithThreads,
} from '@schooly/components/threads';
import { SchoolUserRole } from '@schooly/constants';
import {
  ArrowLeftIcon,
  CrossIcon,
  InboxStubSvg,
  Loading,
  ModalContent,
  ModalMain,
  PlusIcon,
  SimpleButton,
  TabsWithCounters,
} from '@schooly/style';
import { ConsentFormPreview } from 'apps/web/src/components/common/ConsentForms/ConsentFormPreview';
import { STREAM_API_KEY } from 'apps/web/src/config';
import { useRouter } from 'apps/web/src/context/router/useRouter';
import { FC, PropsWithChildren, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { MessagePreviewInfoContent } from '../../../Messages/MessagesPreviewModal/MessageModalPreviewMessageInfo';
import { MessageRecipientsPreviewFromApi } from '../../../Messages/MessagesPreviewModal/MessagesModalPreviewSendTo/MessageRecipientsPreviewFromApi';
import { MessagePreviewRecipientsContent } from '../../../Messages/MessagesPreviewModal/MessagesModalPreviewReadStatistics/MessagesModalPreviewReadStatistics';
import { ThreadCard, ThreadWidgetChannelLoading } from './ThreadCard';
import { useThreadsSidebarData } from './useThreadsSidebarData';
import { MessageModalPreviewSelectedThread } from '../../../Messages/MessagesPreviewModal/MessageModalPreviewThreads/MessageModalPreviewSelectedThread';
import { MessageThreadList } from '../../../Messages/MessagesPreviewModal/MessageModalPreviewThreads/MessageThreadList';
const SKELETON_COUNT = 9;
const MONTH_NONE = 'NONE';

type ThreadsSidebarProps = {
  open: boolean;
  onClose: () => void;
};

export const ThreadsSidebar: FC<ThreadsSidebarProps> = ({ open, onClose }) => {
  return (
    <Drawer
      anchor="right"
      sx={{ p: 0 }}
      open={open}
      onClose={onClose}
      PaperProps={{ role: 'dialog' }}
    >
      {open && <ThreadsSidebarContent onClose={onClose} />}
    </Drawer>
  );
};

const ThreadsSidebarContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [selectedMessage, setSelectedMessage] = useState<MessageForGroup | null>(null);
  const { schoolId = '' } = useAuth();

  const handleClose = useCallback(() => {
    setSelectedMessage(null);
    onClose();
  }, [onClose]);

  const body = (
    <SidebarBody
      selectedMessage={selectedMessage}
      onSelectMessage={setSelectedMessage}
      onClose={handleClose}
    />
  );

  return (
    <Stack width={546} height="100%" pt={2.5}>
      {schoolId && STREAM_API_KEY && selectedMessage ? (
        <WithThreads
          schoolId={schoolId}
          rootEntityType={RootEntityType.MESSAGE}
          rootEntityId={selectedMessage.id}
          apiKey={STREAM_API_KEY}
        >
          {body}
        </WithThreads>
      ) : (
        body
      )}
    </Stack>
  );
};

type SidebarBodyProps = {
  selectedMessage: MessageForGroup | null;
  onSelectMessage: (message: MessageForGroup | null) => void;
  onClose: () => void;
};

const SidebarBody: FC<SidebarBodyProps> = ({ selectedMessage, onSelectMessage, onClose }) => {
  const { formatMessage } = useIntl();
  const { selectedThread, clearThread } = useThreads();

  const title = selectedMessage
    ? selectedMessage.title
    : formatMessage({ id: 'dashboard-teacher-MyThreads' });

  const handleBack = useCallback(() => {
    if (selectedThread) {
      clearThread();
      return;
    }
    onSelectMessage(null);
  }, [selectedThread, clearThread, onSelectMessage]);

  const tooltipTitle = selectedMessage
    ? formatMessage({
        id: selectedThread
          ? 'dashboard-teacher-ThreadsSidebar-BackToMessage'
          : 'dashboard-teacher-ThreadsSidebar-BackToMyThreads',
      })
    : '';

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" px={2.5} pt={2}>
        <Tooltip
          title={tooltipTitle}
          componentsProps={{
            tooltip: {
              sx: {
                margin: '0px !important',
                py: 0.25,
                px: 0.5,
              },
            },
          }}
          arrow={false}
          placement="top"
        >
          <span>
            <SimpleButton
              startIcon={selectedMessage ? <ArrowLeftIcon /> : undefined}
              onClick={handleBack}
              sx={(theme) => ({
                '& .svg-icon path': { color: theme.palette.primary.main },
              })}
            >
              <Typography variant="h2">{title}</Typography>
            </SimpleButton>
          </span>
        </Tooltip>

        <IconButton inverse onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </Stack>
      <ModalMain>
        <ModalContent sx={{ pb: 0 }} active>
          {selectedMessage ? (
            <ThreadDetailView messageId={selectedMessage.id} />
          ) : (
            <ThreadListView onSelectMessage={onSelectMessage} />
          )}
        </ModalContent>
      </ModalMain>
    </>
  );
};

type ThreadListViewProps = {
  onSelectMessage: (message: MessageForGroup) => void;
};

const ThreadListView: FC<ThreadListViewProps> = ({ onSelectMessage }) => {
  const {
    threadsByMonthAndYear,
    unreadMessageIds,
    activityMap,
    loadingActivityIds,
    isEmpty,
    hasNextPage,
    isFetchingNextPage,
    loaderRef,
    isLoading,
  } = useThreadsSidebarData();

  if (isLoading) {
    return (
      <ThreadsSkeleton rowCount={SKELETON_COUNT}>
        <Skeleton variant="rectangular" sx={{ minHeight: 18, width: 60, borderRadius: '24px' }} />
      </ThreadsSkeleton>
    );
  }

  if (isEmpty) {
    return <ThreadsEmptyStub />;
  }

  return (
    <Stack flex={1} overflow="auto">
      <Stack gap={1}>
        {Object.entries(threadsByMonthAndYear).map(([monthAndYear, messages]) => (
          <Box key={monthAndYear}>
            {monthAndYear !== MONTH_NONE && (
              <Typography pt={0.25} pl={1.5} variant="h4" color="common.grey3">
                {monthAndYear}
              </Typography>
            )}
            <Stack pl={0.25}>
              {messages.map((message) => (
                <ThreadCard
                  key={message.id}
                  message={message}
                  isUnread={unreadMessageIds.has(message.id)}
                  lastActivity={activityMap[message.id]}
                  isActivityLoading={loadingActivityIds.has(message.id)}
                  onClick={() => onSelectMessage(message)}
                />
              ))}
            </Stack>
          </Box>
        ))}

        {hasNextPage && (
          <Box py={2}>
            {isFetchingNextPage && <ThreadsSkeleton rowCount={Math.ceil(SKELETON_COUNT / 2)} />}
            <div ref={loaderRef} style={{ height: 1 }} />
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

const ThreadsEmptyStub: FC = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { setBackground } = useRouter();

  const handleCreateThread = useCallback(() => {
    setBackground('messages');
    navigate('/messages/new');
  }, [navigate, setBackground]);

  return (
    <Stack height="100%" alignItems="center" justifyContent="center">
      <Box sx={{ '& svg': { width: 200, height: 200 } }}>
        <InboxStubSvg />
      </Box>

      <Stack alignItems="center" gap={1} pt={11}>
        <Typography variant="h1" textAlign="center">
          {formatMessage({ id: 'dashboard-teacher-ThreadsSidebar-EmptyTitle' })}
        </Typography>
        <Box>
          <Typography variant="h3" color="common.grey2" textAlign="center">
            {formatMessage({ id: 'dashboard-teacher-ThreadsSidebar-EmptySubtitleLine1' })}
          </Typography>
          <Typography variant="h3" color="common.grey2" textAlign="center">
            {formatMessage({ id: 'dashboard-teacher-ThreadsSidebar-EmptySubtitleLine2' })}
          </Typography>
        </Box>

        <Button sx={{ mt: 2 }} startIcon={<PlusIcon />} onClick={handleCreateThread}>
          {formatMessage({ id: 'dashboard-teacher-ThreadsSidebar-CreateThread' })}
        </Button>
      </Stack>
    </Stack>
  );
};

const ThreadsSkeleton: FC<PropsWithChildren<{ rowCount: number }>> = ({ rowCount, children }) => {
  return (
    <Stack flex={1} overflow="auto" gap={2}>
      {children}
      {Array.from({ length: rowCount }).map((_, i) => (
        <Skeleton key={i} variant="rectangular" sx={{ minHeight: 64, borderRadius: 1 }} />
      ))}
    </Stack>
  );
};

type ThreadDetailViewProps = {
  messageId: string;
};

enum Tab {
  Threads,
  MessageInfo,
  ReadingStatistics,
  ConsentForm,
}

const ThreadDetailView: FC<ThreadDetailViewProps> = ({ messageId }) => {
  const [activeTab, setActiveTab] = useState(Tab.Threads);
  const { selectedThread } = useThreads();

  const { data, isLoading } = useGetMessageQuery(
    { messageId },
    { enabled: !!messageId, refetchOnMount: 'always' },
  );
  const { formatMessage } = useIntl();

  const renderContent = () => {
    if (!data) return null;
    const { message, message_recipients } = data;
    switch (activeTab) {
      case Tab.Threads:
        return <ThreadsTab message={message} />;
      case Tab.MessageInfo:
        return (
          <MessagePreviewInfoContent
            creator={message.creator}
            publisher={message.publisher}
            body={message.body}
            attachments={message.attachments ?? []}
            recipientsPreview={<MessageRecipientsPreviewFromApi data={data} />}
          />
        );
      case Tab.ReadingStatistics: {
        const actualParents =
          message_recipients.filter(
            (recipient) => recipient.school_user_relation_role === SchoolUserRole.Parent,
          ) ?? [];

        return <MessagePreviewRecipientsContent actualParents={actualParents} />;
      }
      case Tab.ConsentForm:
        if (!message.consent_form) return null;
        return (
          <ConsentFormPreview
            consentFormShort={message.consent_form}
            label={formatMessage({ id: 'consentForms-title' })}
          />
        );
    }
  };

  return (
    <Stack height="100%" minHeight={0}>
      <TabsWithCounters
        tabItems={[
          { labelId: 'messages-Threads', value: Tab.Threads },
          { labelId: 'messages-MessageInfo', value: Tab.MessageInfo },
          { labelId: 'messages-ReadingStatistics', value: Tab.ReadingStatistics },
          ...(data?.message.consent_form
            ? [{ labelId: 'consentForms-title', value: Tab.ConsentForm }]
            : []),
        ]}
        isLoading={isLoading}
        onChange={(_, v: number) => setActiveTab(v)}
        activeTab={activeTab}
        sx={{
          pt: 1.5,
          pl: 2.75,
          '& .MuiTabs-flexContainer': { overflow: 'scroll' },
          mb: 0,
          '& .MuiTab-root': {
            height: 28,
            minHeight: 0,
          },
        }}
      />
      <Divider sx={{ mt: 1.25 }} />

      <Stack flex={1} minHeight={0} pt={activeTab === Tab.Threads && selectedThread ? 0 : 1.75}>
        {isLoading ? <Loading /> : renderContent()}
      </Stack>
    </Stack>
  );
};

const EmptyStateIndicator = () => (
  <Stack height="100%" alignItems="center" justifyContent="center">
    <NoMessagesStub />
  </Stack>
);

const ThreadsTab: FC<{ message: Message }> = ({ message }) => {
  const { schoolId = '' } = useAuth();
  const threadUser = useThreadUser();

  if (!threadUser || !schoolId || !STREAM_API_KEY) {
    return (
      <Stack height="100%" justifyContent="center" alignItems="center" width="100%">
        <Loading />
      </Stack>
    );
  }

  return <ThreadsTabContent message={message} schoolId={schoolId} threadUser={threadUser} />;
};

type ThreadsTabContentProps = {
  message: Message;
  schoolId: string;
  threadUser: ThreadUser;
};

const ThreadsTabContent: FC<ThreadsTabContentProps> = ({ message, schoolId, threadUser }) => {
  const onThreadClick = useSelectThread({ currentUser: threadUser });

  const {
    selectedThread,
    isUserConnecting,
    isChannelCreating,
    isThreadMembersUpdating,
    isChannelLoading,
  } = useThreads();

  const isConnecting =
    isUserConnecting || isChannelCreating || isThreadMembersUpdating || isChannelLoading;

  if (selectedThread || isConnecting) {
    return (
      <MessageModalPreviewSelectedThread
        isThreadFetching={false}
        hideChannelPreview
        contentSx={{
          backgroundColor: 'common.white',
          '.str-chat__virtual-list, .str-chat__container, .str-chat__channel': {
            backgroundColor: 'common.white',
          },
        }}
        inputSx={{ pl: 0.5, pr: 0 }}
        ChannelLoadingComponent={ThreadWidgetChannelLoading}
        EmptyStateIndicator={EmptyStateIndicator}
      />
    );
  }

  return (
    <MessageThreadList
      schoolId={schoolId}
      rootEntityType={RootEntityType.MESSAGE}
      rootEntityId={message.id}
      onThreadClick={onThreadClick}
    />
  );
};
